import { ReactNode } from 'react';

import { AssetClassToLoanType } from 'app-level/config/assetClassTypes';
import { formatCurrency } from 'configs/columns';
import { Link } from 'react-router-dom';

import { LoanField, SortableField, AssetClass as GqlAssetClass } from '__generated__/globalTypes';

import {
  LoanDatatableLoan as Loan, //TODO uncoment when BE will be ready
} from './__generated__/LoanDatatableLoan';
import { ButtonField } from './ButtonField';
import PoolsColumn from './PoolsColumn';
import { checkIsValue } from '../../../utils/dataDisplay';

type LoanColConfig<T extends Loan, K extends keyof T> = {
  string: string;
  sortSelector?: SortableField;
  numeric?: boolean;
  display: (value: T[K], other: object) => string | ReactNode;
};

type CRELoan = AssetClassToLoanType[GqlAssetClass.CRE];

//TODO temporary until BE is ready
export interface LoanDatatableLoan_CRELoan_Borrower {
  name: string | null;
  type: string | null;
  borrower_type: string | null;
  tax_id: string | null;
  summary: string | null;
  revolving_debt_cents: number | null;
  guarantors: {
    name: string | null;
    credit_score: number | null;
  }[];
}

export interface LoanDatatableLoan_CRELoan_Collateral {
  name: string | null;
  type: string | null;
  class: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  summary: string | null;
  building_count: number | null;
  units_count: number | null;
  rentable_square_feet: number | null;
  construction_date_year: number | null;
  metropolitan_statistical_area: string | null;
  occupancy_rate_bps: number | null;
  valuation_as_is_date_seconds: number | null;
  valuation_stabilized_date_seconds: number | null;
  value_as_is_cents: number | null;
  value_stabilized_cents: number | null;
}

export interface LoanDatatableLoan_CRELoan {
  account_id: string | null;
  id: string;
  city: string | null;
  postal_code: string | null;
  state: string | null;
  asset_class: 'CRE';
  current_balance_cents: Long;
  original_balance_cents: Long;
  net_operating_income_cents: number | null;
  interest_rate_bps: number | null;
  servicing_rate_bps: number | null;
  loan_terms_months: number | null;
  remaining_loan_terms_months: number | null;
  age_months: number | null;
  product: string | null;
  property_description: string | null;
  property_year: number | null;
  property_type: string | null;
  occupancy: string | null;
  purpose: string | null;
  cra: boolean;
  ltv_bps: number | null;
  dscr: number | null;
  loan_risk_grade: string | null;
  days_delinquent: number | null;
  prepayment_penalty_schedule: number[];

  county?: string | null;
  company_id?: string | null;
  currency_code?: string | null;
  accrued_unpaid_interest_cents?: number | null;
  amortization_term_months?: number | null;
  next_payment_date_seconds?: number | null;
  origination_date_seconds?: number | null;
  first_payment_date_seconds?: number | null;
  last_updated_date_seconds?: number | null;
  maturity_date_seconds?: number | null;
  capitalization_rate_up?: number | null;
  capitalization_rate_down?: number | null;
  average_rent_per_square_foot?: number | null;
  total_rentable_square_feet?: number | null;
  debt_service_coverage_ratio_date_seconds?: number | null;
  last_review_date_seconds?: number | null;
  interest_days?: number | null;
  next_review_date_seconds?: number | null;
  standard_metropolitan_statistical_area_code?: string | null;
  owner_user?: string | null;
  listing_id?: string | null;
  census_tract?: string | null;
  borrower?: LoanDatatableLoan_CRELoan_Borrower;
  collateral?: LoanDatatableLoan_CRELoan_Collateral;
  future_advance_cents?: number | null;
  initial_advance_cents?: number | null;
  total_commitment_cents?: number | null;
  participated_amount_cents?: number | null;
  price_per_unit_cents?: number | null;
  tenant_description?: string | null;
  property_summary?: string | null;
  sale_condition?: string | null;
  repayment_source?: string | null;
  secondary_payment_source?: string | null;
  investment_highlights?: string[] | null;
  days_past_due?: number | null;
  property_details?: {
    property_type: string | null;
    appraised_value_cents: number | null;
    appraisal_date_seconds: number | null;
    occupancy_type: string | null;
    lien_position: number | null;
    property_sq_footage: number | null;
    year_built: number | null;
  };
  times_past_due_30_to_59_days?: number | null;
  times_past_due_60_to_89_days?: number | null;
  times_past_due_over_90_days?: number | null;
  custom_fields?: {
    special_servicing: boolean;
    workout_status: string | null;
    environmental_issues: string | null;
  };

  //left from old one
  // __typename: 'CRELoan' | 'MemberBusinessLoan';
  // id: string;
  borrower_credit_score?: number | null;
  // company: LoanDatatableLoan_CRELoan_company;
  // first_payment_date: DateTime;
  // maturity_date: DateTime | null;
  // last_updated_date: DateTime;
}
//TODO temporary until BE is ready

export type LoanColumnsConfig<T extends Loan> = {
  [field in keyof T]?: LoanColConfig<T, field>;
};

// Helper function to get asset class URL segment from GraphQL asset class
const getAssetClassUrlSegment = (assetClass: keyof AssetClassToLoanType): string => {
  switch (assetClass) {
    case GqlAssetClass.AUTO:
      return 'auto';
    case GqlAssetClass.HOME:
      return 'home';
    case 'HOME_ARM':
      return 'home';
    case GqlAssetClass.CRE:
      return 'cre';
    case GqlAssetClass.UNSECPERSONAL:
      return 'unsecuredpersonal';
    default:
      return 'home'; // fallback
  }
};

const getSharedColumns = (
  assetClass: keyof AssetClassToLoanType,
): { [field in keyof Loan]?: LoanColConfig<Loan, field> } => ({
  [LoanField.account_id]: {
    string: `LOAN ID`,
    sortSelector: SortableField.account_id,
    numeric: false,
    display: (value, { onClick }: { onClick?: () => void }) =>
      onClick ? <ButtonField label={value} onClick={onClick} /> : value || '',
  },

  pools: {
    string: `POOLS`,
    display: (value, loan) => {
      return <PoolsColumn pools={value || []} assetClass={(loan as any).asset_class} />;
    },
  },
  [LoanField.original_balance_cents]: {
    string: `ORIG. BAL`,
    sortSelector: SortableField.original_balance_cents,
    numeric: true,
    display: (cents) => formatCurrency(cents) ?? `-`,
  },
  [LoanField.current_balance_cents]: {
    string: `UPB`,
    sortSelector: SortableField.current_balance_cents,
    numeric: true,
    display: (cents) => formatCurrency(cents) ?? `-`,
  },
  [LoanField.age_months]: {
    string: `LOAN AGE`,
    sortSelector: SortableField.age_months,
    numeric: true,
    display: (value) => value?.toLocaleString() ?? `-`,
  },
  [LoanField.remaining_loan_terms_months]: {
    string: `MATURITY`,
    sortSelector: SortableField.remaining_loan_terms_months,
    numeric: true,
    display: (value) => value?.toLocaleString() ?? `-`,
  },
  [LoanField.interest_rate_bps]: {
    string: 'INTEREST RATE',
    sortSelector: SortableField.interest_rate_bps,
    numeric: true,
    display: (bps) => (checkIsValue(bps) ? `${((bps ?? 0) / 100).toFixed(3)}%` : `-`),
  },
  //@ts-ignore
  [LoanField.borrower_credit_score]: {
    string: 'CREDIT SCORE',
    sortSelector: SortableField.borrower_credit_score,
    numeric: true,
    display: (value) => value?.toString() ?? '-',
  },
  [LoanField.product]: {
    string: 'PRODUCT',
    sortSelector: SortableField.product,
    display: (value) => value || `-`,
  },
});

type HomeLoan = AssetClassToLoanType[GqlAssetClass.HOME];
export const homeColumnsConfig: {
  [field in keyof HomeLoan]?: LoanColConfig<HomeLoan, field>;
} = {
  ...getSharedColumns(GqlAssetClass.HOME),
  [LoanField.ltv_bps]: {
    string: 'LTV',
    sortSelector: SortableField.ltv_bps,
    numeric: true,
    display: (bps) => (checkIsValue(bps) ? `${((bps ?? 0) / 100).toFixed(2)}%` : '-'),
  },
  [LoanField.dti_bps]: {
    string: 'DTI',
    sortSelector: SortableField.dti_bps,
    numeric: true,
    display: (bps) => (checkIsValue(bps) ? `${((bps ?? 0) / 100).toFixed(2)}%` : '-'),
  },
  [LoanField.state]: {
    string: 'STATE',
    display: (value) => value || '-',
  },
  [LoanField.occupancy]: {
    string: 'OCCUPANCY',
    display: (value) => value || '-',
  },
  [LoanField.purpose]: {
    string: 'PURPOSE',
    display: (value) => value || '-',
  },
  [LoanField.cra]: {
    string: 'CRA',
    numeric: false,
    display: (value) => (value ? 'True' : 'False'),
  },
};
export type HomeARMLoan = AssetClassToLoanType['HOME_ARM'];

export const homeARMColumnsConfig: {
  [field in keyof HomeLoan]?: LoanColConfig<HomeLoan, field>;
} = {
  ...homeColumnsConfig,
  [LoanField.irap_months]: {
    string: 'IRAP',
    numeric: true,
    display: (value) => value || '-',
  },
  [LoanField.rap_months]: {
    string: 'RAP',
    numeric: true,
    display: (value) => value || '-',
  },
  [LoanField.periodic_cap_rate_bps]: {
    string: 'Periodic Cap',
    display: (bps) => (checkIsValue(bps) ? `${((bps ?? 0) / 100).toFixed(2)}%` : '-'),
  },
  [LoanField.lifetime_cap_rate_bps]: {
    string: 'Lifetime Cap',
    numeric: false,
    display: (bps) => (checkIsValue(bps) ? `${((bps ?? 0) / 100).toFixed(2)}%` : '-'),
  },
  [LoanField.lifetime_floor_rate_bps]: {
    string: 'Lifetime Floor',
    numeric: false,
    display: (bps) => (checkIsValue(bps) ? `${((bps ?? 0) / 100).toFixed(2)}%` : '-'),
  },
  [LoanField.interest_only]: {
    string: 'Interest Only',
    display: (value) => (value ? 'True' : 'False'),
  },
  [LoanField.io_period_months]: {
    string: 'I/O Term',
    numeric: true,
    display: (value) => value || '-',
  },
  [LoanField.amortization_term_months]: {
    string: 'Amortization Term',
    numeric: true,
    display: (value) => value || '-',
  },
  [LoanField.balloon_term_months]: {
    string: 'Balloon Term',
    numeric: true,
    display: (value) => value || '-',
  },
  [LoanField.arm_margin_bps]: {
    string: 'ARM Margin',
    numeric: true,
    display: (bps) => (checkIsValue(bps) ? `${((bps ?? 0) / 100).toFixed(2)}%` : '-'),
  },
  [LoanField.arm_index]: {
    string: 'ARM Index',
    display: (value) => value || '-',
  },
  [LoanField.arm_index_rate_bps]: {
    string: 'ARM Index Rate',
    numeric: true,
    display: (bps) => (checkIsValue(bps) ? `${((bps ?? 0) / 100).toFixed(2)}%` : '-'),
  },
};

type AutoLoan = AssetClassToLoanType[GqlAssetClass.AUTO];
export const autoColumnsConfig: {
  [field in keyof AutoLoan]?: LoanColConfig<AutoLoan, field>;
} = {
  ...getSharedColumns(GqlAssetClass.AUTO),
  [LoanField.ltv_bps]: {
    string: 'LTV',
    sortSelector: SortableField.ltv_bps,
    numeric: true,
    display: (bps) => (checkIsValue(bps) ? `${((bps ?? 0) / 100).toFixed(2)}%` : '-'),
  },
  [LoanField.dti_bps]: {
    string: 'DTI',
    sortSelector: SortableField.dti_bps,
    numeric: true,
    display: (bps) => (checkIsValue(bps) ? `${((bps ?? 0) / 100).toFixed(2)}%` : '-'),
  },
  [LoanField.state]: {
    string: 'STATE',
    display: (value) => value || '-',
  },
  [LoanField.auto_original_value_cents]: {
    string: `O VAL`,
    sortSelector: SortableField.auto_original_value_cents,
    numeric: true,
    display: (cents) => (checkIsValue(cents) ? formatCurrency(cents) : '-'),
  },
  [LoanField.auto_vin]: {
    string: `VIN`,
    display: (value) => value || '-',
  },
};

export const creColumnsConfig: {
  [field in keyof CRELoan]?: LoanColConfig<CRELoan, field>;
} = {
  ...getSharedColumns(GqlAssetClass.CRE),
  [LoanField.state]: {
    string: 'STATE',
    display: (value) => value || '-',
  },
  // city: {
  //   string: 'CITY',
  //   display: (value) => value || '-',
  // },
  // postal_code: {
  //   string: 'ZIP',
  //   display: (value) => value || '-',
  // },
  // ltv: {
  //   string: 'LTV',
  //   numeric: true,
  //   sortSelector: SortableField.ltv,
  //   display: (value) =>
  //     checkIsValue(value) ? `${((value ?? 0) * 100).toFixed(2)}%` : '-',
  // },
  // property_description: {
  //   string: 'PROPERTY',
  //   display: (value) => value || '-',
  // },
  // property_year: {
  //   string: 'YEAR BUILT',
  //   numeric: true,
  //   display: (value) => value?.toString() ?? '-',
  // },
  // occupancy: {
  //   string: 'OCCUPANCY',
  //   display: (value) => value || '-',
  // },
  // purpose: {
  //   string: 'PURPOSE',
  //   display: (value) => value || '-',
  // },
  // cra: {
  //   string: 'CRA',
  //   display: (value) => (value ? 'True' : 'False'),
  // },
  // dscr: {
  //   string: 'DSCR',
  //   numeric: true,
  //   display: (value) => (checkIsValue(value) ? value?.toFixed(2) : '-'),
  // },
  // prepayment_penalty_schedule: {
  //   string: 'PREPAY SCHEDULE',
  //   display: (value) => (Array.isArray(value) ? value.join(', ') : '-'),
  // },
};

export const creColumnsOrder: (keyof CRELoan)[] = [
  LoanField.account_id,
  'pools',
  LoanField.original_balance_cents,
  LoanField.current_balance_cents,
  LoanField.age_months,
  LoanField.remaining_loan_terms_months,
  LoanField.interest_rate_bps,
  LoanField.product,

  LoanField.state,
  // LoanField.city,
  // LoanField.postal_code,
  // LoanField.ltv,
  // 'property_description',
  // 'property_year',
  // LoanField.occupancy,
  // LoanField.purpose,
  // LoanField.cra,
  // 'dscr',
  // 'prepayment_penalty_schedule',
];

type UnsecPersonalLoan = AssetClassToLoanType[GqlAssetClass.UNSECPERSONAL];

export const unsecperosnalColumnsConfig: {
  [field in keyof UnsecPersonalLoan]?: LoanColConfig<UnsecPersonalLoan, field>;
} = {
  ...getSharedColumns(GqlAssetClass.UNSECPERSONAL),
  [LoanField.dti_bps]: {
    string: 'DTI',
    sortSelector: SortableField.dti_bps,
    numeric: true,
    display: (bps) => (checkIsValue(bps) ? `${((bps ?? 0) / 100).toFixed(2)}%` : '-'),
  },
  [LoanField.purpose]: {
    string: 'PURPOSE',
    display: (value) => value || '-',
  },
};

export const unsecpersonalColumnsOrder: (keyof UnsecPersonalLoan)[] = [
  LoanField.account_id,
  'pools',
  LoanField.original_balance_cents,
  LoanField.current_balance_cents,
  LoanField.age_months,
  LoanField.remaining_loan_terms_months,
  LoanField.interest_rate_bps,
  LoanField.borrower_credit_score,
  LoanField.dti_bps,
  LoanField.purpose,
  LoanField.product,
];

export const homeColumnsOrder: (keyof HomeLoan)[] = [
  LoanField.account_id,
  'pools',
  LoanField.original_balance_cents,
  LoanField.current_balance_cents,
  LoanField.state,
  LoanField.age_months,
  LoanField.remaining_loan_terms_months,
  LoanField.interest_rate_bps,
  LoanField.borrower_credit_score,
  LoanField.ltv_bps,
  LoanField.dti_bps,
  LoanField.product,
  LoanField.occupancy,
  LoanField.purpose,
  LoanField.cra,
];

export const homeARMColumnsOrder: (keyof AssetClassToLoanType[GqlAssetClass.HOME])[] = [
  ...homeColumnsOrder,
  LoanField.irap_months,
  LoanField.rap_months,
  LoanField.periodic_cap_rate_bps,
  LoanField.lifetime_cap_rate_bps,
  LoanField.lifetime_floor_rate_bps,
  LoanField.interest_only,
  LoanField.io_period_months,
  LoanField.amortization_term_months,
  LoanField.balloon_term_months,
  LoanField.arm_margin_bps,
  LoanField.arm_index,
  LoanField.arm_index_rate_bps,
];

export const autoColumnsOrder: (keyof AutoLoan)[] = [
  LoanField.account_id,
  'pools',
  LoanField.state,
  LoanField.original_balance_cents,
  LoanField.current_balance_cents,
  LoanField.age_months,
  LoanField.remaining_loan_terms_months,
  LoanField.interest_rate_bps,
  LoanField.borrower_credit_score,
  LoanField.ltv_bps,
  LoanField.dti_bps,
  LoanField.product,
  LoanField.auto_original_value_cents,
  LoanField.auto_vin,
];

export type LoanKeys = keyof (HomeLoan & AutoLoan);

export const assetClassToColumnsConfig: {
  [key in keyof AssetClassToLoanType]: {
    order: (keyof AssetClassToLoanType[key])[];
    config: LoanColumnsConfig<AssetClassToLoanType[key]>;
  } | null;
} = {
  [GqlAssetClass.HOME]: {
    config: homeARMColumnsConfig,
    order: homeARMColumnsOrder,
  },
  HOME_ARM: { config: homeARMColumnsConfig, order: homeARMColumnsOrder },
  [GqlAssetClass.AUTO]: { config: autoColumnsConfig, order: autoColumnsOrder },
  [GqlAssetClass.UNSECPERSONAL]: {
    config: unsecperosnalColumnsConfig,
    order: unsecpersonalColumnsOrder,
  },
  [GqlAssetClass.AIRCRAFT]: null,
  [GqlAssetClass.BOAT]: null,
  [GqlAssetClass.CRE]: {
    config: creColumnsConfig,
    order: creColumnsOrder,
  },
  [GqlAssetClass.MARINE]: null,
  [GqlAssetClass.MBL]: null,
  [GqlAssetClass.MOTORCYCLE]: null,
  [GqlAssetClass.REVOLVING]: null,
  [GqlAssetClass.RV]: null,
  [GqlAssetClass.SOLAR]: null,
  [GqlAssetClass.WAREHOUSE]: null,
};
